import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import PasswordInput from '@/components/PasswordInput';

const BACKEND_URL = '';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const forced = searchParams.get('forced') === 'true';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      if (forced && !token) {
        // Forced change via session (login redirect flow)
        const res = await fetch(`${BACKEND_URL}/api/auth/force-change-password`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ new_password: password }),
        });
        if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      } else {
        // Token-based reset (email link flow)
        const res = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, new_password: password }),
        });
        if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      }
      setDone(true);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  if (!token && !forced) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="p-8 max-w-md text-center">
          <h2 className="text-lg font-semibold text-slate-800 mb-2">Invalid Reset Link</h2>
          <p className="text-sm text-slate-500 mb-4">This link is invalid or has expired.</p>
          <Button onClick={() => navigate('/login')} className="bg-brand-navy hover:bg-brand-dark text-white">
            Back to Login
          </Button>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="p-8 max-w-md text-center" data-testid="reset-success">
          <div className="w-14 h-14 bg-brand-sage/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="h-7 w-7 text-brand-sage" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-slate-800 mb-2">Password Reset</h2>
          <p className="text-sm text-slate-500 mb-4">Your password has been updated. You can now log in.</p>
          <Button data-testid="go-to-login-btn" onClick={() => navigate('/login')}
            className="bg-gradient-to-r from-brand-navy to-brand-sage text-white">
            Go to Login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <Card className="p-8 max-w-md w-full" data-testid="reset-password-form">
        <h2 className="text-xl font-bold font-heading gradient-text mb-1">
          {forced ? 'Password Change Required' : 'Set New Password'}
        </h2>
        <p className="text-sm text-slate-500 mb-6">
          {forced ? 'An administrator has required you to change your password. Please enter a new password below.' : 'Enter your new password below.'}
        </p>
        <form onSubmit={handleReset} className="space-y-4">
          <PasswordInput data-testid="reset-password-input" placeholder="New password (min 6 chars)"
            value={password} onChange={e => setPassword(e.target.value)} required className="h-11 border-slate-200 pr-10" />
          <PasswordInput data-testid="reset-confirm-input" placeholder="Confirm new password"
            value={confirm} onChange={e => setConfirm(e.target.value)} required className="h-11 border-slate-200 pr-10" />
          <Button data-testid="reset-submit-btn" type="submit" disabled={loading}
            className="w-full h-11 bg-gradient-to-r from-brand-navy to-brand-sage hover:from-brand-dark hover:to-brand-sage text-white">
            {loading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
